import { useEffect, useState } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  Spinner,
  Banner,
  Divider,
  Icon,
  ProgressBar,
  TextField,
} from "@shopify/polaris";
import { CheckCircleIcon } from "@shopify/polaris-icons";
import {
  previewPayable,
  authorizePayouts,
  waitForPayout,
  messageFor,
  conceptError,
  foldConcept,
} from "../../utility/pagos";
import ClabeBadge from "./ClabeBadge";

// Confirmation dialog for a SPEI payout.
//
// Every figure shown here is computed by pagos-svc from the database — the
// amount, the beneficiary, the CLABE. Nothing comes from the calling page,
// because a dialog that displays numbers the browser invented is not a
// control, it is decoration. The one editable field is the SPEI concepto:
// pagos-svc proposes it ("CA11671", "Factura 2E3F4A5B"…) and whatever
// is in the box when "Pagar" is pressed is what the payee reads.
//
// After authorising, the dialog STAYS OPEN and reports the payout's real
// progress. A payout is not instant (worker pickup -> signed transfer ->
// webhook, ~13s live), and the first version closed and reloaded the whole
// page, so the person who had just moved real money saw a flash and no
// outcome. Money movement has to narrate itself.
const STAGES = {
  queued: { label: "Autorizado. En cola para envío…", progress: 25 },
  submitted: { label: "Enviando transferencia SPEI…", progress: 65 },
  succeeded: { label: "¡Pago enviado!", progress: 100 },
};

// `nounLabel` / `beneficiaryLabel` word the copy for other payable kinds
// (e.g. "liquidación" / "trasladista"); defaults keep the service wording.
function SpeiPaymentModal({
  open,
  payableKind = "service",
  payableId,
  onClose,
  onAuthorized,
  nounLabel = "servicio",
  beneficiaryLabel = "proveedor",
}) {
  const [preview, setPreview] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [phase, setPhase] = useState("confirm"); // confirm | working | done
  const [stage, setStage] = useState(null);
  const [result, setResult] = useState(null);
  const [concept, setConcept] = useState("");

  useEffect(() => {
    if (!open) {
      setPreview(undefined);
      setError(undefined);
      setPhase("confirm");
      setStage(null);
      setResult(null);
      setConcept("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const p = await previewPayable(payableKind, payableId);
        if (!cancelled) {
          setPreview(p);
          setConcept(p.comment || "");
        }
      } catch (err) {
        if (!cancelled) setError(err.message || messageFor(err.code));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, payableKind, payableId]);

  const confirm = async () => {
    setPhase("working");
    setStage("queued");
    setError(undefined);
    try {
      const typed = foldConcept(concept);
      const { results } = await authorizePayouts([
        {
          payable_kind: payableKind,
          payable_id: String(payableId),
          ...(typed !== preview.comment ? { comment: typed } : {}),
        },
      ]);
      const r = results?.[0];
      if (!r?.ok) {
        setError(messageFor(r?.error, r?.message));
        setPhase("confirm");
        return;
      }

      const final = await waitForPayout(r.payout_id, (status) => setStage(status));
      setResult(final);

      if (final.status === "succeeded") {
        setStage("succeeded");
        setPhase("done");
        // Refresh the page's data in place — never a full reload.
        onAuthorized?.(r.payout_id, final);
      } else if (final.status === "awaiting_funds") {
        // Parked, not failed: the authorization stands and the sweep is
        // already moving the missing money; it sends itself when it lands.
        setPhase("done");
        onAuthorized?.(r.payout_id, final);
      } else if (final.status === "pending_timeout") {
        // Deliberately not an error: the transfer is probably still in flight.
        setPhase("done");
        onAuthorized?.(r.payout_id, final);
      } else {
        setError(
          final.status === "returned"
            ? "La transferencia fue devuelta por el banco receptor."
            : `La transferencia no se completó (${final.status}).`
        );
        setPhase("confirm");
        onAuthorized?.(r.payout_id, final);
      }
    } catch (err) {
      // We do not know whether the payout was created. Say so plainly rather
      // than inviting a second attempt.
      setError(
        `${err.message || messageFor(err.code)} Verifica el estado del ${nounLabel} antes de reintentar.`
      );
      setPhase("confirm");
    }
  };

  const row = (label, value) => (
    <InlineStack align="space-between" blockAlign="center" gap={400}>
      <Text tone="subdued">{label}</Text>
      {value}
    </InlineStack>
  );

  const working = phase === "working";
  const done = phase === "done";
  const stageInfo = STAGES[stage] || { label: "Procesando…", progress: 10 };

  return (
    <Modal
      size="small"
      open={open}
      // Closing mid-flight would hide the outcome of a real transfer.
      onClose={working ? () => {} : onClose}
      title={done ? (result?.status === "awaiting_funds" ? "Pago autorizado" : "Pago enviado") : "Pagar por SPEI"}
      primaryAction={
        done
          ? { content: "Listo", onAction: onClose }
          : {
              content: preview ? `Pagar ${preview.amount_display}` : "Pagar",
              onAction: confirm,
              loading: working,
              disabled: !preview || !preview.payable || working || !!conceptError(concept),
            }
      }
      secondaryActions={
        done ? [] : [{ content: "Cancelar", onAction: onClose, disabled: working }]
      }
    >
      <Modal.Section>
        {error && (
          <BlockStack gap={300}>
            <Banner tone="critical">{error}</Banner>
          </BlockStack>
        )}

        {(working || done) && (
          <BlockStack gap={300}>
            <InlineStack gap={200} blockAlign="center">
              {done && result?.status === "succeeded" ? (
                <Icon source={CheckCircleIcon} tone="success" />
              ) : (
                <Spinner size="small" />
              )}
              <Text variant="headingSm" as="p">
                {result?.status === "pending_timeout"
                  ? "Sigue en proceso"
                  : result?.status === "awaiting_funds"
                    ? "Autorizado, esperando fondos"
                    : stageInfo.label}
              </Text>
            </InlineStack>
            <ProgressBar
              progress={["pending_timeout", "awaiting_funds"].includes(result?.status) ? 65 : stageInfo.progress}
              tone={done && result?.status === "succeeded" ? "success" : "primary"}
              size="small"
            />
            {result?.status === "awaiting_funds" && (
              <Banner tone="info">
                La cuenta no tenía saldo suficiente. Ya se pidió el fondeo desde
                la cuenta General; el SPEI sale solo en cuanto llegue (normalmente
                en segundos). No lo vuelvas a enviar.
              </Banner>
            )}
            {result?.status === "pending_timeout" && (
              <Banner tone="info">
                La transferencia sigue en curso. No la vuelvas a enviar: el
                estado del {nounLabel} se actualizará solo en cuanto el banco
                confirme.
              </Banner>
            )}
            {done && result?.tracking_key && (
              <Text tone="subdued" variant="bodySm">
                Clave de rastreo: {result.tracking_key}
              </Text>
            )}
            {working && (
              <Text tone="subdued" variant="bodySm">
                No cierres esta ventana. Suele tardar unos segundos.
              </Text>
            )}
          </BlockStack>
        )}

        {phase === "confirm" && !preview && !error ? (
          <InlineStack gap={200} blockAlign="center">
            <Spinner size="small" />
            <Text tone="subdued">Consultando el {nounLabel}…</Text>
          </InlineStack>
        ) : phase === "confirm" && preview ? (
          <BlockStack gap={400}>
            {!preview.payable && preview.blocked && (
              <Banner tone="warning" title="No se puede pagar todavía">
                {messageFor(preview.blocked.code, preview.blocked.message)}
              </Banner>
            )}

            <BlockStack gap={100}>
              <Text tone="subdued">Importe</Text>
              <Text variant="headingLg" as="p">{preview.amount_display}</Text>
            </BlockStack>

            <Divider />

            <BlockStack gap={300}>
              {row(
                "Beneficiario",
                <Text fontWeight="semibold" alignment="end">{preview.holder_name || "—"}</Text>
              )}
              {row("CLABE", <Text numeric alignment="end">{`•••• ${preview.clabe_last4}`}</Text>)}
              {row("Cuenta", <ClabeBadge status={preview.payable ? "verified" : undefined} />)}
              {row("Referencia", <Text numeric alignment="end">{preview.reference_id}</Text>)}
            </BlockStack>

            <TextField
              label="Concepto"
              value={concept}
              onChange={setConcept}
              error={concept !== preview.comment ? conceptError(concept) || undefined : undefined}
              maxLength={40}
              showCharacterCount
              autoComplete="off"
              disabled={!preview.payable}
            />

            {preview.funds?.available_display && (
              <Text
                tone={preview.funds.sufficient === false ? "critical" : "subdued"}
                variant="bodySm"
              >
                Saldo disponible: {preview.funds.available_display}
                {preview.funds.sufficient === false && preview.payable
                  ? " — no alcanza: el pago esperará a que se fondee la cuenta y saldrá solo."
                  : ""}
              </Text>
            )}

            <Text tone="subdued" variant="bodySm">
              El importe y la cuenta destino los calcula el servidor a partir del
              {` ${nounLabel} y del ${beneficiaryLabel} verificado.`}
            </Text>
          </BlockStack>
        ) : null}
      </Modal.Section>
    </Modal>
  );
}

export default SpeiPaymentModal;
